import { useDatoClient } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { orderBy } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import ChatGPTService from '../../services/ChatGPTService';
import { Circle } from '../../styles/mainStyle';
import BackIcon from '../../svgs/BackIcon';
import CloseIcon from '../../svgs/CloseIcon';
import PhotoIcon from '../../svgs/PhotoIcon';
import { PhotoTab } from '../../types.ts/story';
import applyImageOptimizations from '../../utility/applyImageOptimizations';
import { generateImagesWithGPT } from '../../utility/processGPTData';
import { getAttributionForPhoto, searchPhotos } from '../../utility/unsplash';
import SearchInput from '../sidepanel/SearchInput';
import SpinningLoading from '../SpinningLoading';
import EnlargedImageRenderProp from './EnlargedImageRenderProp';
import ShareableImageView from './ShareableImageView';
import { AuthorAttribution, UnsplashAttribution } from './UnsplashAttributions';

import type { ImageKey, ImageWithType } from '../../types.ts/general';
import type { Artifact } from '../../types.ts/story';
import type { Attribution } from '../../utility/unsplash';
import type { Dispatch, SetStateAction } from 'react';

const logger = LogManager.getLogger('component:PhotosModal');

type Props = {
  openPrevModal: () => void;
  onCloseSelf: () => void;
  modalItemType?: ImageKey;
  otherFields?: Array<
    'ai' | 'stock' | 'quotes' | 'org_photos' | 'org_logos' | 'org_videos'
  >;
  externalPhotos?: {
    ai?:
      | (Img & {
          id: string;
        })[]
      | undefined;
    stock: Img[] | undefined;
  };
  isLoading?: boolean;
  setSelectedImage: (img: ImageWithType[ImageKey] | null) => void;
  selectedImageUrl: string | null;
  keywords?: string[];
  setKeywords?: Dispatch<SetStateAction<string[]>>;
  dallePrompt?: string;
  setDallePrompt?: Dispatch<SetStateAction<string>>;
  TopComponent?: React.ComponentType<{ tab: PhotoTab }>;
  BottomComponent?: React.ComponentType<{ tab: PhotoTab }>;
  searchBarRadius?: string;
  searchText?: string;
  setSearchText?: Dispatch<SetStateAction<string>>;
  origin?: string;
  showDescription?: boolean;
};

export type Img = {
  url: string;
  alt: string;
  width: string;
  height: string;
  description?: string;
  attribution?: Attribution;
  createdAt?: string;
};

type QuoteImg = Img & {
  quote: string;
  storytellerName?: string;
  shareableImageId: string;
};

type Asset<T extends PhotoTab> = {
  key: T;
  value: T extends PhotoTab.ai ? (Img & { id: string })[] : Img[];
};

const SMALL_PHOTO_HEIGHT = 98;

const mapArtifactToPhotoAsset = (artifact: Artifact) =>
  ({
    id: artifact.id,
    url: artifact.url,
    alt: artifact.title,
    width: artifact.width,
    height: artifact.height,
    description: artifact.title,
    createdAt: artifact._createdAt,
  }) as Img;

const prepareArtifacts = (artifacts?: Artifact[]) =>
  orderBy(
    artifacts?.filter(a => a.responsiveImage).map(mapArtifactToPhotoAsset) ||
      [],
    'createdAt',
    'desc',
  );

const prepareVideoArtifacts = (artifacts?: Artifact[]) =>
  orderBy(artifacts?.map(mapArtifactToPhotoAsset) || [], 'createdAt', 'desc');

const PhotoModal = observer((props: Props) => {
  const {
    otherFields = [],
    searchBarRadius,
    modalItemType,
    TopComponent,
    BottomComponent,
  } = props;
  const videoCreator = useVideoCreatorStore();
  const datoCientStore = useDatoClient();

  let hasOrgLogos = otherFields.includes('org_logos');
  const [stockItems, setStockItems] = useState<Img[] | undefined>(undefined);
  const [aiItems, setAiItems] = useState<Img[] | undefined>(undefined);
  const [stockLoading, setStockLoading] = useState<boolean>(false);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const { keywords, setKeywords } = props;
  const [searched, setSearched] = useState<Record<ImageKey, boolean>>();

  let hasStocks = !!(
    otherFields.includes('stock') || props.externalPhotos?.stock
  );
  let hasAis =
    !!(otherFields.includes('ai') || props.externalPhotos?.ai) &&
    !videoCreator.disableAiImageGenerate;
  let hasQuotes = otherFields.includes('quotes');
  let hasOrgArtifacts = otherFields.includes('org_photos');
  let hasOrgVideos = false;
  if (hasOrgLogos) {
    hasStocks = false;
    hasAis = false;
    hasQuotes = false;
    hasOrgArtifacts = false;
    hasOrgVideos = false;
  }

  if (otherFields.includes('org_videos')) {
    hasOrgVideos = true;
    hasOrgLogos = false;
  }

  // Keep search terms in state
  const [artifactSearchTerm, setArtifactSearchTerm] = useState('');
  const [orgArtifactSearchTerm, setOrgArtifactSearchTerm] = useState('');
  const [orgLogoSearchTerm, setOrgLogoSearchTerm] = useState('');
  const [orgVideoSearchTerm, setOrgVideoSearchTerm] = useState('');
  const [storyVideoSearchTerm, setStoryVideoSearchTerm] = useState('');

  // Get original data from source
  const artifactsOriginal = useMemo(
    () => prepareArtifacts(videoCreator.story?.storyArtifacts),
    [videoCreator.story?.storyArtifacts],
  );

  const stock = useMemo(
    () => prepareArtifacts(videoCreator?.story?.storyAssets),
    [videoCreator?.story?.storyAssets],
  );

  const ai = useMemo(
    () => prepareArtifacts(videoCreator?.story?.aiPhotos),
    [videoCreator?.story?.aiPhotos],
  );

  const allReferencingShowcases = videoCreator?.story?._allReferencingShowcases;
  const _orgArtifacts = allReferencingShowcases?.flatMap(
    album => album.organizationArtifacts || [],
  );
  const organizationArtifactsOriginal = useMemo(
    () => prepareArtifacts(_orgArtifacts),
    [_orgArtifacts],
  );

  const _orgLogos = allReferencingShowcases?.flatMap(
    album => album.organizationLogos || [],
  );
  const organizationLogosOriginal = useMemo(
    () => prepareArtifacts(_orgLogos),
    [_orgLogos],
  );

  // Compute filtered lists based on search terms
  const artifact = useMemo(() => {
    if (!artifactSearchTerm) return artifactsOriginal;
    return artifactsOriginal.filter(
      a =>
        a.description?.toLowerCase().includes(artifactSearchTerm.toLowerCase()),
    );
  }, [artifactsOriginal, artifactSearchTerm]);

  const organizationArtifacts = useMemo(() => {
    if (!orgArtifactSearchTerm) return organizationArtifactsOriginal;
    return organizationArtifactsOriginal.filter(
      a =>
        a.description
          ?.toLowerCase()
          .includes(orgArtifactSearchTerm.toLowerCase()),
    );
  }, [organizationArtifactsOriginal, orgArtifactSearchTerm]);

  const organizationLogos = useMemo(() => {
    if (!orgLogoSearchTerm) return organizationLogosOriginal;
    return organizationLogosOriginal.filter(
      a =>
        a.description?.toLowerCase().includes(orgLogoSearchTerm.toLowerCase()),
    );
  }, [organizationLogosOriginal, orgLogoSearchTerm]);

  const _storyVideos = useMemo(
    () => prepareVideoArtifacts(videoCreator.story?.storyArtifactsVideo),
    [videoCreator.story?.storyArtifactsVideo],
  );

  const storyVideos = useMemo(() => {
    if (!storyVideoSearchTerm) return _storyVideos;
    return _storyVideos.filter(
      a =>
        a.description
          ?.toLowerCase()
          .includes(storyVideoSearchTerm.toLowerCase()),
    );
  }, [_storyVideos, storyVideoSearchTerm]);

  const _orgVideos = useMemo(() => {
    return prepareVideoArtifacts(
      allReferencingShowcases?.flatMap(
        album => album.organizationArtifactsVideo || [],
      ),
    );
  }, [allReferencingShowcases]);

  const organizationVideos = useMemo(() => {
    if (!orgVideoSearchTerm) return _orgVideos;
    return _orgVideos.filter(
      a =>
        a.description?.toLowerCase().includes(orgVideoSearchTerm.toLowerCase()),
    );
  }, [_orgVideos, orgVideoSearchTerm]);

  const handleGoBack = () => {
    props.openPrevModal();
    props.onCloseSelf();
  };

  const assetsAndTabs = React.useMemo(() => {
    let assets: Asset<PhotoTab>[];
    let tabHeader: PhotoTab[];

    if (hasOrgVideos) {
      assets = [
        {
          key: PhotoTab.orgVideos,
          value: organizationVideos,
        },
        {
          key: PhotoTab.storyVideos,
          value: storyVideos,
        },
      ];
      tabHeader = [PhotoTab.orgVideos, PhotoTab.storyVideos];
      return { assets, tabHeader };
    }

    if (hasOrgLogos) {
      assets = [
        {
          key: PhotoTab.logo,
          value: organizationLogos,
        },
      ];
      tabHeader = [PhotoTab.logo];
    } else {
      assets = [
        {
          key: PhotoTab.stock,
          value: stockItems || stock || props.externalPhotos?.stock || [],
        },
        { key: PhotoTab.artifact, value: artifact },
      ];
      tabHeader = [PhotoTab.artifact, PhotoTab.stock];
    }

    if (hasAis) {
      assets.push({
        key: PhotoTab.ai,
        value: aiItems || ai || props.externalPhotos?.ai || [],
      });
      tabHeader.push(PhotoTab.ai);
    }

    if (hasQuotes) {
      const quoteImages = videoCreator.story?.shareableImages
        ?.filter(s => s.imagefile)
        .map(s => ({
          imagefile: s.imagefile,
          quote: s.quote,
          storytellerName: s.storytellerName,
          id: s.id,
        }))
        ?.map(a => ({
          id: a?.imagefile?.id,
          alt: a?.imagefile?.title,
          url: a?.imagefile?.url,
          width: a?.imagefile?.width,
          height: a?.imagefile?.height,
          quote: a.quote,
          storytellerName: a.storytellerName,
          shareableImageId: a.id,
        })) as QuoteImg[];

      assets.push({
        key: PhotoTab.quote,
        value: quoteImages || [],
      });
      tabHeader.push(PhotoTab.quote);
    }

    if (hasOrgArtifacts) {
      assets.push({
        key: PhotoTab.orgArtifact,
        value: organizationArtifacts,
      });
      tabHeader.splice(1, 0, PhotoTab.orgArtifact);
    }

    return { assets, tabHeader };
  }, [
    hasOrgVideos,
    hasOrgLogos,
    hasAis,
    hasQuotes,
    hasOrgArtifacts,
    stockItems,
    stock,
    props.externalPhotos?.stock,
    artifact,
    aiItems,
    ai,
    props.externalPhotos?.ai,
    organizationLogos,
    organizationArtifacts,
    organizationVideos,
    storyVideos,
    videoCreator.story?.shareableImages,
  ]);

  const assets = assetsAndTabs.assets;
  const tabHeader = assetsAndTabs.tabHeader;

  const [tab, setTab] = useState(() => {
    if (modalItemType === 'stock') return PhotoTab.stock;
    if (hasOrgLogos) return PhotoTab.logo;
    if (artifact.length) return PhotoTab.artifact;
    if (hasOrgArtifacts && organizationArtifacts.length)
      return PhotoTab.orgArtifact;
    if (hasStocks) return PhotoTab.stock;
    if (hasOrgVideos) return PhotoTab.storyVideos;
    return PhotoTab.stock;
  });

  const [unsplashSearch, setUnsplashSearch] = useState(
    keywords ? keywords.join(' ') : '',
  );
  const [unsplashPage, setUnsplashPage] = useState(1);
  const handleSearchStock = async (textValue: string) => {
    setUnsplashSearch(textValue);
    setUnsplashPage(1);
  };
  useEffect(() => {
    const fetchUnsplashPhotos = async () => {
      if (!unsplashSearch) {
        setStockItems(undefined);
        setUnsplashPage(1);
        setUnsplashSearch('');
        return;
      }
      try {
        setStockLoading(true);
        const { photos } = await searchPhotos({
          query: unsplashSearch,
          orientation: 'landscape',
          page: unsplashPage,
          perPage: 9,
        });
        const stockData = (photos?.map(res => ({
          id: res.id,
          url: res.urls.regular,
          alt: res.alt_description,
          width: res.width,
          height: res.height,
          description: res.description || res.alt_description,
          attribution: getAttributionForPhoto(res),
        })) || []) as unknown as Img[];
        setStockItems(_items =>
          unsplashPage === 1 ? stockData : (_items || []).concat(stockData),
        );
        const _searched = { ...(searched || {}), stock: true } as Record<
          ImageKey,
          boolean
        >;
        setSearched(_searched);
      } catch (error) {
        logger.error('Error fetching Unsplash photos', { error });
      } finally {
        setStockLoading(false);
      }
    };
    fetchUnsplashPhotos();
  }, [unsplashSearch, unsplashPage]);

  const handleAiSearch = async (dallePrompt: string) => {
    if (!dallePrompt.length) {
      return setAiItems([]);
    }
    try {
      setAiLoading(true);
      const gptService = new ChatGPTService(
        videoCreator,
        datoCientStore.aiPromptRepository,
      );
      const arborStylePrompt =
        await gptService.fetchAiPrompt('Arbor Photo Style');

      // Build the prompt with speaker context if specified
      let promptText = dallePrompt;

      // Final prompt with Arbor style
      let prompt = `${promptText} ${arborStylePrompt?.description || ''}`;

      const result = await generateImagesWithGPT(prompt, 2);
      setAiItems(result);
    } catch (error) {
      console.log('An error occurred in searching ai', error);
    } finally {
      setAiLoading(false);
    }
  };

  const asset = assets.find(asset => asset.key === tab);

  const removeKeywords = async (keyword: string) => {
    let updatedKeywords = keywords?.filter(el => el != keyword);
    if (setKeywords && updatedKeywords) {
      setKeywords(updatedKeywords);
      setUnsplashSearch(updatedKeywords.join(' '));
      setUnsplashPage(1);
    }
  };

  const handleArtifactSearch = (text: string) => {
    setArtifactSearchTerm(text);
  };

  const handleOrganizationArtifactSearch = (text: string) => {
    setOrgArtifactSearchTerm(text);
  };

  const handleOrganizationLogoSearch = (text: string) => {
    setOrgLogoSearchTerm(text);
  };

  const handleOrganizationVideoSearch = (text: string) => {
    setOrgVideoSearchTerm(text);
  };

  const handleStoryVideoSearch = (text: string) => {
    setStoryVideoSearchTerm(text);
  };

  const renderKeyWordSearch = () => (
    <SearchWrapper keywords={keywords}>
      <SearchInput
        iconRight={true}
        placeholder="Search key words"
        handleAction={handleSearchStock}
        radius={searchBarRadius}
      />
      {hasStocks && (
        <KeywordsWrapper>
          {keywords?.map(keyword => {
            return (
              <Keyword>
                <span className="text"> {keyword?.trim()}</span>
                <div className="close" onClick={() => removeKeywords(keyword)}>
                  <CloseIcon width="7" height="8" strokeColor="#D7D7E1" />
                </div>
              </Keyword>
            );
          })}
        </KeywordsWrapper>
      )}
    </SearchWrapper>
  );

  const renderAiSearch = () => (
    <SearchWrapper>
      <SearchInput
        iconRight={true}
        hideClearAll
        placeholder="Search key words"
        handleAction={handleAiSearch}
        initialValue={props.dallePrompt}
        expandHeight
        radius={searchBarRadius}
      />
    </SearchWrapper>
  );

  const renderArtifactSearch = () => (
    <SearchWrapper>
      <SearchInput
        iconRight={true}
        hideClearAll
        placeholder="Search a photo's description"
        handleAction={handleArtifactSearch}
        expandHeight
        radius={searchBarRadius}
      />
    </SearchWrapper>
  );

  const renderOrganizationArtifactSearch = () => (
    <SearchWrapper>
      <SearchInput
        iconRight={true}
        hideClearAll
        placeholder="Search a photo's description"
        handleAction={handleOrganizationArtifactSearch}
        expandHeight
        radius={searchBarRadius}
      />
    </SearchWrapper>
  );

  const renderOrganizationLogoSearch = () => (
    <SearchWrapper>
      <SearchInput
        iconRight={true}
        hideClearAll
        placeholder="Search a logo's description"
        handleAction={handleOrganizationLogoSearch}
        expandHeight
        radius={searchBarRadius}
      />
    </SearchWrapper>
  );

  const renderOrganizationVideoSearch = () => (
    <SearchWrapper>
      <SearchInput
        iconRight={true}
        hideClearAll
        placeholder="Search a video's description"
        handleAction={handleOrganizationVideoSearch}
        expandHeight
        radius={searchBarRadius}
      />
    </SearchWrapper>
  );

  const renderStoryVideoSearch = () => (
    <SearchWrapper>
      <SearchInput
        iconRight={true}
        hideClearAll
        placeholder="Search a video's description"
        handleAction={handleStoryVideoSearch}
        expandHeight
        radius={searchBarRadius}
      />
    </SearchWrapper>
  );

  const renderItemList = (asset: Asset<PhotoTab>) => (
    <Category origin={props.origin}>
      <ItemList>
        {asset.value.map((s, i) => (
          <div key={s.url}>
            <EnlargedImageRenderProp
              src={s.url}
              smallPhotoHeight={SMALL_PHOTO_HEIGHT}
              direction={i % 3 === 0 ? 'right' : 'left'}
            >
              {({ enlargedState, setEnlargedState, renderEnlargedImage }) => (
                <>
                  <ImageWrapper
                    onClick={() => {
                      if (props.selectedImageUrl === s.url) return null;
                      props.setSelectedImage({
                        ...s,
                        type: asset.key.toLowerCase(),
                      } as ImageWithType[ImageKey]);
                    }}
                    onMouseEnter={e => {
                      setEnlargedState(e.currentTarget.getBoundingClientRect());
                    }}
                    onMouseMove={e => {
                      if (!enlargedState) {
                        setEnlargedState(
                          e.currentTarget.getBoundingClientRect(),
                        );
                      }
                    }}
                    onMouseLeave={() => {
                      setEnlargedState(null);
                    }}
                  >
                    {asset.key === PhotoTab.quote ? (
                      <ShareableImageView
                        background={s.url}
                        width="auto"
                        height="98px"
                        imageRef={null}
                        quote={(s as QuoteImg).quote}
                        fontSize="10px"
                        lineHeight="12px"
                        quoteSize={{ width: '14px', height: '12px' }}
                        storyTellerDim={0.5}
                      />
                    ) : asset.key === PhotoTab.storyVideos ||
                      asset.key === PhotoTab.orgVideos ? (
                      <>
                        <Circle isSelected={props.selectedImageUrl === s.url} />
                        <Video src={s.url} title={s.description} />
                      </>
                    ) : (
                      <>
                        <Circle isSelected={props.selectedImageUrl === s.url} />
                        <Image
                          loading="lazy"
                          src={applyImageOptimizations(s.url, { width: 140 })}
                          alt={s.alt}
                          type={asset.key}
                        />
                      </>
                    )}
                  </ImageWrapper>
                  {enlargedState && renderEnlargedImage()}
                </>
              )}
            </EnlargedImageRenderProp>
            {props.showDescription && (
              <Description>{s.description}</Description>
            )}
            {renderImgLevelAttribution(s, asset.key)}
          </div>
        ))}
      </ItemList>
      {renderTabLevelAttribution(asset)}
      {asset.key === PhotoTab.stock && unsplashSearch && (
        <LoadMoreButton
          disabled={stockLoading}
          onClick={() => setUnsplashPage(cur => cur + 1)}
        >
          Load more photos
        </LoadMoreButton>
      )}
    </Category>
  );

  const renderSpinner = (text: string) => (
    <SpinningLoading
      customStyle={{ top: 0 }}
      Ico={<PhotoIcon strokeColor="#17C964" strokeWidth={2} />}
      text={text}
    />
  );

  const renderTabLevelAttribution = (
    asset: Asset<PhotoTab>,
  ): React.ReactNode => {
    if (!asset?.value?.some(v => v.attribution)) return null;

    switch (asset.key) {
      case PhotoTab.stock:
        return (
          <AttributionRow>
            <UnsplashAttribution />
          </AttributionRow>
        );
      default:
        return null;
    }
  };

  const renderImgLevelAttribution = (
    img: Img,
    assetKey: Asset<PhotoTab>['key'],
  ): React.ReactNode => {
    if (!img.attribution) return null;

    switch (assetKey) {
      case PhotoTab.stock:
        return (
          <AuthorAttribution
            author={img.attribution.author}
            link={img.attribution.link}
          />
        );
      default:
        return null;
    }
  };

  const renderContent = () => {
    if (unsplashPage === 1 && stockLoading && tab === PhotoTab.stock) {
      return renderSpinner('Loading stock images...');
    } else if ((!hasAis || aiLoading) && tab === PhotoTab.ai) {
      return renderSpinner('Loading Ai images...');
    } else if (asset?.value?.length) {
      return renderItemList(asset);
    } else {
      return (
        <NoContent>
          {tab === PhotoTab.quote
            ? 'You have no saved shareable images'
            : tab === PhotoTab.artifact || tab === PhotoTab.orgArtifact
              ? ''
              : tab === PhotoTab.stock
                ? searched?.stock
                  ? 'Stock did not generate results'
                  : ''
                : 'Describe the photo you want to see and hit enter'}
        </NoContent>
      );
    }
  };

  return (
    <Main onKeyDown={e => e.stopPropagation()}>
      <Back role="button" onClick={handleGoBack}>
        <BackIcon strokeColor="#F3E9D7" />
      </Back>

      {TopComponent && <TopComponent tab={tab} />}

      <Tabs>
        {tabHeader.map(t => (
          <Tab
            key={t}
            isSelected={tab === t}
            onClick={() => {
              setTab(t);
              props.setSelectedImage(null);
            }}
          >
            {t === PhotoTab.quote ? 'Shareable Images' : t}
          </Tab>
        ))}
      </Tabs>

      {tab === PhotoTab.artifact && renderArtifactSearch()}
      {tab === PhotoTab.stock && renderKeyWordSearch()}
      {tab === PhotoTab.ai && renderAiSearch()}
      {tab === PhotoTab.orgArtifact && renderOrganizationArtifactSearch()}
      {tab === PhotoTab.logo && renderOrganizationLogoSearch()}
      {tab === PhotoTab.orgVideos && renderOrganizationVideoSearch()}
      {tab === PhotoTab.storyVideos && renderStoryVideoSearch()}

      {props.isLoading && renderSpinner('Saving Ai photo. Please wait...')}

      <Content origin={props.origin}>
        {renderContent()}
        <ContentBottom>
          {BottomComponent && <BottomComponent tab={tab} />}
        </ContentBottom>
      </Content>
    </Main>
  );
});

export default PhotoModal;

const Main = styled.div``;

const Back = styled.div`
  position: absolute;
  top: 15px;
  left: 16px;
  cursor: pointer;
`;

const Content = styled.div<{ origin?: string }>`
  position: relative;
  height: 300px;
  overflow: auto;
  display: flex;
  flex-direction: column;

  ${props =>
    props.origin === 'quotecard' &&
    css`
      padding: 0 20px;
      max-height: 400px;
      min-height: 340px;
    `}
`;

const ContentBottom = styled.div`
  margin-top: auto;
  padding: 0 20px;
`;

const SearchWrapper = styled.div<{ keywords?: string[] }>`
  padding: 0 20px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: ${props => props.keywords?.length && '15px'};
`;

const Category = styled.div<{ origin?: string }>`
  text-align: left;

  ${props =>
    props.origin !== 'quotecard' &&
    css`
      margin: 5px 0;
      overflow: auto;
      padding: 0 20px;
      flex: 1;
    `}
`;

const ItemList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 15px;
`;
const ImageWrapper = styled.div`
  height: ${SMALL_PHOTO_HEIGHT}px;
  border-radius: 10px;
  position: relative;
  cursor: pointer;
  &::before {
    content: '';
    background: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const Image = styled.img<{ type: PhotoTab }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: ${props => props.type !== PhotoTab.quote && 'top'};
  border-radius: 10px;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  border-radius: 10px;
`;

const LoadMoreButton = styled.button`
  margin: 24px auto 5px;
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  background-color: #03041a;
  color: #17c964;
  border: 1px solid #17c964;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  outline: 0;
  transition: 0.2s;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Description = styled.div`
  margin-top: 5px;
  font-weight: 400;
  font-size: 10px;
  line-height: 14px;
  color: #f3e9d7;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`;

const Tab = styled.div<{ isSelected?: boolean }>`
  color: ${props => (props.isSelected ? '#F2D093' : '#f3e9d7')};
  text-decoration: ${props => (props.isSelected ? 'underline' : '')};
  cursor: pointer;
  font-size: 12px;
  font-weight: ${props => (props.isSelected ? '800' : '400')};
`;

const Tabs = styled.div`
  display: flex;
  gap: 20px;
  margin-left: 20px;
  margin-bottom: 10px;
`;

const NoContent = styled.div`
  color: #f3e9d7;
  font-weight: 600;
  margin-top: 20px;
  font-size: 14px;
  display: flex;
  justify-content: center;
`;

const Keyword = styled.span`
  color: #d7d7e1;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #d7d7e1;
  border-radius: 40px;
  padding: 8px 16px;
  align-items: center;
  white-space: nowrap;
  display: flex;
  gap: 5px;
  .close {
    cursor: pointer;
  }
`;

const KeywordsWrapper = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-start;
  width: 100%;
  padding-left: 5px;
  overflow: auto;
`;

const AttributionRow = styled.div`
  margin: 24px 0 8px 0;
  display: flex;
  justify-content: center;
`;
